import { Platform } from 'react-native';
import SimpleStorage from '../utils/simpleStorage';
import { smsService } from './smsService';
import emailService from './emailService';
import type { StaffInvite, StaffMembership, StaffRole } from '../types/staff';
import { permissionsForRole } from '../types/staff';
import membershipService from './membershipService';

const INVITE_BASE_URL = 'https://bookapt.io';
const STORAGE_KEY = 'staff_invites_cache';

function nowIso() {
  return new Date().toISOString();
}

function normalizeEmail(email?: string) {
  return (email || '').trim().toLowerCase();
}

export function buildStaffInviteUrl(inviteId: string, token: string, baseUrl: string = INVITE_BASE_URL): string {
  const params = new URLSearchParams({ inviteId, token });
  return `${baseUrl.replace(/\/$/, '')}/staff-invite?${params.toString()}`;
}

function buildSMSMessage(businessName: string, role: StaffRole, inviteUrl: string): string {
  const roleLabel = role === 'site_manager' ? 'Site Manager' : role === 'worker' ? 'Staff' : 'Owner';
  return `BookAPT invite: You've been invited to join ${businessName} as ${roleLabel}. Accept: ${inviteUrl}`;
}

function buildEmailSubject(businessName: string): string {
  return `You're invited to join ${businessName} on BookAPT`;
}

function buildEmailText(businessName: string, role: StaffRole, inviteUrl: string): string {
  const roleLabel = role === 'site_manager' ? 'Site Manager' : role === 'worker' ? 'Staff' : 'Owner';
  return `You've been invited to join ${businessName} on BookAPT as ${roleLabel}.\n\nAccept: ${inviteUrl}\n\nBookAPT`;
}

function buildEmailHtml(businessName: string, role: StaffRole, inviteUrl: string): string {
  const roleLabel = role === 'site_manager' ? 'Site Manager' : role === 'worker' ? 'Staff' : 'Owner';
  return `
<!DOCTYPE html>
<html>
  <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
      <div style="background: #007AFF; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0;">
        <h2 style="margin: 0;">BookAPT staff invitation</h2>
      </div>
      <div style="padding: 24px; background: #f9f9f9; border: 1px solid #E5E5E7; border-top: none; border-radius: 0 0 8px 8px;">
        <p>You've been invited to join <strong>${businessName}</strong> as <strong>${roleLabel}</strong>.</p>
        <div style="text-align: center; margin: 20px 0;">
          <a href="${inviteUrl}" style="background: #007AFF; color: white; padding: 12px 22px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: bold;">
            Accept invite
          </a>
        </div>
        <p style="color: #666; font-size: 13px;">Or copy this link: ${inviteUrl}</p>
      </div>
    </div>
  </body>
</html>`;
}

class StaffInvitationService {
  async createInvite(params: {
    businessId: string;
    businessName: string;
    invitedByUserId: string;
    email?: string;
    phone?: string;
    role: StaffRole;
    permissions?: StaffInvite['permissions'];
    locationIds?: string[];
    workerId?: string;
  }): Promise<StaffInvite> {
    const id = `staff_inv_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    const token = Math.random().toString(36).slice(2, 10);
    const emailLower = normalizeEmail(params.email);

    const invite: StaffInvite = {
      id,
      businessId: params.businessId,
      invitedByUserId: params.invitedByUserId,
      email: params.email?.trim() || undefined,
      phone: params.phone?.trim() || undefined,
      emailLower: emailLower || undefined,
      role: params.role,
      permissions: params.permissions ?? permissionsForRole(params.role),
      locationIds: params.locationIds,
      workerId: params.workerId,
      token,
      status: 'pending',
      createdAt: nowIso(),
    };

    await this.saveInvite(invite);
    return invite;
  }

  async sendInvite(invite: StaffInvite, businessName: string): Promise<{ smsSent: boolean; emailSent: boolean; inviteUrl: string }> {
    const inviteUrl = buildStaffInviteUrl(invite.id, invite.token);

    let smsSent = false;
    let emailSent = false;

    if (invite.phone) {
      try {
        smsSent = await smsService.sendSMS(invite.phone, buildSMSMessage(businessName, invite.role, inviteUrl), 'general');
      } catch {
        // ignore
      }
    }

    if (invite.email) {
      try {
        const subject = buildEmailSubject(businessName);
        const html = buildEmailHtml(businessName, invite.role, inviteUrl);
        const text = buildEmailText(businessName, invite.role, inviteUrl);
        const result = await emailService.sendEmail({ to: invite.email, subject, html, text });
        emailSent = result.success;
      } catch {
        // ignore
      }
    }

    return { smsSent, emailSent, inviteUrl };
  }

  async getInvite(inviteId: string): Promise<StaffInvite | null> {
    // cache first
    try {
      const raw = await SimpleStorage.getItem(STORAGE_KEY);
      if (raw) {
        const list = JSON.parse(raw) as StaffInvite[];
        const found = list.find((i) => i.id === inviteId);
        if (found) return found;
      }
    } catch {
      // ignore
    }

    try {
      if (Platform.OS === 'web') {
        const { doc, getDoc } = await import('firebase/firestore');
        const { getWebDb } = await import('./unifiedAuthService');
        const db = getWebDb();
        if (!db) return null;
        const snap = await getDoc(doc(db, 'staffInvites', inviteId));
        if (!snap.exists()) return null;
        return { id: snap.id, ...(snap.data() as any) } as StaffInvite;
      }
      const { getMobileFirestore, initializeFirebase } = require('./unifiedAuthService');
      initializeFirebase?.();
      const getFs = getMobileFirestore?.();
      if (typeof getFs !== 'function') return null;
      const db = getFs();
      const snap = await db.collection('staffInvites').doc(inviteId).get();
      if (!snap.exists) return null;
      return { id: snap.id, ...snap.data() } as StaffInvite;
    } catch {
      return null;
    }
  }

  async acceptInvite(params: { inviteId: string; token: string; userId: string }): Promise<{ success: boolean; error?: string; membership?: StaffMembership }> {
    const invite = await this.getInvite(params.inviteId);
    if (!invite) return { success: false, error: 'Invite not found or expired.' };
    if (invite.token !== params.token) return { success: false, error: 'Invalid invite token.' };
    if (invite.status !== 'pending') return { success: false, error: 'Invite already used or revoked.' };

    const membership: StaffMembership = {
      id: invite.businessId,
      userId: params.userId,
      businessId: invite.businessId,
      role: invite.role,
      permissions: invite.permissions,
      locationIds: invite.locationIds,
      workerId: invite.workerId,
      status: 'active',
      createdAt: nowIso(),
      updatedAt: nowIso(),
    };

    // Write membership
    await membershipService.upsertMembership(membership);

    // Mark invite accepted (best-effort)
    try {
      const updates = {
        status: 'accepted',
        acceptedAt: nowIso(),
        acceptedByUserId: params.userId,
      };
      if (Platform.OS === 'web') {
        const { doc, updateDoc, setDoc } = await import('firebase/firestore');
        const { getWebDb } = await import('./unifiedAuthService');
        const db = getWebDb();
        if (db) {
          // prefer updateDoc, but fall back to setDoc merge if missing
          try {
            await updateDoc(doc(db, 'staffInvites', invite.id), updates as any);
          } catch {
            await setDoc(doc(db, 'staffInvites', invite.id), updates as any, { merge: true });
          }
        }
      } else {
        const { getMobileFirestore, initializeFirebase } = require('./unifiedAuthService');
        initializeFirebase?.();
        const getFs = getMobileFirestore?.();
        if (typeof getFs === 'function') {
          const db = getFs();
          await db.collection('staffInvites').doc(invite.id).set(updates, { merge: true });
        }
      }
    } catch {
      // ignore
    }

    return { success: true, membership };
  }

  private async saveInvite(invite: StaffInvite): Promise<void> {
    // cache
    try {
      const raw = await SimpleStorage.getItem(STORAGE_KEY);
      const list = raw ? (JSON.parse(raw) as StaffInvite[]) : [];
      list.unshift(invite);
      await SimpleStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, 50)));
    } catch {
      // ignore
    }

    // Firestore
    try {
      if (Platform.OS === 'web') {
        const { doc, setDoc } = await import('firebase/firestore');
        const { getWebDb } = await import('./unifiedAuthService');
        const db = getWebDb();
        if (!db) return;
        await setDoc(doc(db, 'staffInvites', invite.id), invite);
      } else {
        const { getMobileFirestore, initializeFirebase } = require('./unifiedAuthService');
        initializeFirebase?.();
        const getFs = getMobileFirestore?.();
        if (typeof getFs !== 'function') return;
        const db = getFs();
        await db.collection('staffInvites').doc(invite.id).set(invite);
      }
    } catch {
      // ignore
    }
  }
}

export const staffInvitationService = new StaffInvitationService();
export default staffInvitationService;

